// Printable statutory documents for the Emergency module.
//
// Opens a plain, self-contained window and prints it. Deliberately not styled
// with the app's theme: these are legal and police documents that get filed and
// photocopied, so they want black text on white paper and nothing else. Colour,
// shadows and brand furniture would survive to the printout and help nobody.

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** A label/value block. Rows with no value are dropped rather than printed blank. */
export function detailBlock(pairs) {
  const rows = pairs
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(
      ([label, value]) =>
        `<tr><th>${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`
    )
    .join("");
  return `<table class="kv">${rows}</table>`;
}

/**
 * Open `body` as a print-ready document.
 *
 * Returns false when the browser blocked the popup, so the caller can tell the
 * user rather than appearing to do nothing.
 */
export function printDocument({ title, subtitle, body }) {
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) return false;

  win.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    font-family: Georgia, "Times New Roman", serif;
    color: #000; background: #fff;
    margin: 32px; line-height: 1.5; font-size: 13px;
  }
  h1 { font-size: 18px; margin: 0 0 2px; }
  .sub { font-size: 12px; color: #333; margin-bottom: 18px; }
  h2 {
    font-size: 13px; text-transform: uppercase; letter-spacing: .06em;
    border-bottom: 1px solid #000; padding-bottom: 4px;
    margin: 22px 0 10px;
  }
  table.kv { border-collapse: collapse; width: 100%; }
  table.kv th {
    text-align: left; width: 210px; font-weight: normal;
    color: #333; padding: 4px 12px 4px 0; vertical-align: top;
  }
  table.kv td { padding: 4px 0; vertical-align: top; }
  ol { margin: 0; padding-left: 20px; }
  ol li { margin-bottom: 8px; }
  .note { margin-top: 26px; font-size: 11px; color: #444; font-style: italic; }
  .sig { margin-top: 44px; display: flex; gap: 60px; }
  .sig div { flex: 1; border-top: 1px solid #000; padding-top: 5px; font-size: 11px; }
  @media print { body { margin: 0; } }
</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  ${subtitle ? `<div class="sub">${escapeHtml(subtitle)}</div>` : ""}
  ${body}
  <div class="sig">
    <div>Signature of recording staff</div>
    <div>Date and time</div>
  </div>
</body>
</html>`);
  win.document.close();
  win.focus();
  win.print();
  return true;
}

export { escapeHtml };
